import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/system';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { RfidApprovalsResponse, useAsyncAction, useRfid } from '../../../../../hooks';
import { Icons } from '../../../../../stories/atoms';
import { RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../../translations';
import { isFailureResponse } from '../../../../../utils';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import { muiTheme } from '../../../../../theme';
import { isMultiDayInterval } from '../../../util';
import { MetricItem } from './MetricItem';

export type RfidApprovalsWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
};

export const RfidApprovalsWidget: FC<RfidApprovalsWidgetProps> = observer(({ interval, currentDate }) => {
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const { t } = useTranslation();

  const { rfidApprovals: rfidApprovalsTranslations } = homeTranslations;
  const { fetchRfidApprovalsData } = useRfid();

  const {
    result: rfidApprovals,
    isLoading: rfidApprovalsLoading,
    isError: rfidApprovalsError,
    triggerAction: triggerFetchRfidApprovals,
  } = useAsyncAction<RfidApprovalsResponse>();

  const fetchRfidApprovals = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });

    const response = await fetchRfidApprovalsData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [interval, currentDate]);

  const fetchWidgetData = () => {
    triggerFetchRfidApprovals(fetchRfidApprovals);
  };

  useEffect(() => {
    fetchWidgetData();
  }, [currentDate]);

  const isNoData =
    !rfidApprovalsLoading &&
    !rfidApprovalsError &&
    rfidApprovals?.negative.totalPieces === 0 &&
    rfidApprovals?.positive.totalPieces === 0;

  const isDaily = !isMultiDayInterval(interval);

  return (
    <WidgetCardContent
      headerIcon={Icons.PROFILE}
      headerText={t(rfidApprovalsTranslations.title.key, rfidApprovalsTranslations.title.defaultValue)}
      subHeaderText={t(rfidApprovalsTranslations.subTitle.key, rfidApprovalsTranslations.subTitle.defaultValue)}
      toolTipText={t(rfidApprovalsTranslations.tooltip.key, rfidApprovalsTranslations.tooltip.defaultValue)}
      isLoading={rfidApprovalsLoading}
      isError={rfidApprovalsError}
      errorText={t(rfidApprovalsTranslations.error.key, rfidApprovalsTranslations.error.defaultValue)}
      errorRetryHandler={fetchWidgetData}
      lastUpdatedDate={rfidApprovals?.lastUpdated}
      noDataText={t(rfidApprovalsTranslations.noData.key, rfidApprovalsTranslations.noData.defaultValue)}
      isNoData={isNoData}
      content={
        <RowFlexContainer
          flex={1}
          minHeight={[0]}
          padding={[4]}
          gap={[4]}
          data-testid="rfid-approvals-wrapper"
          flexDirection={isMobile ? 'column' : 'row'}
        >
          {rfidApprovals && (
            <>
              <MetricItem
                isDaily={isDaily}
                percentValue={rfidApprovals.negative.difference}
                titleText={t(
                  rfidApprovalsTranslations.negativeTitle.key,
                  rfidApprovalsTranslations.negativeTitle.defaultValue,
                )}
                pieces={rfidApprovals.negative.approvedPieces}
                additionalPieces={rfidApprovals.negative.missingPieces}
                descriptionText={t(
                  rfidApprovalsTranslations.negativeDescription.key,
                  rfidApprovalsTranslations.negativeDescription.defaultValue,
                )}
                approvalRateText={t(
                  rfidApprovalsTranslations.approvalRate.key,
                  rfidApprovalsTranslations.approvalRate.defaultValue,
                )}
                approvalRate={rfidApprovals.negative.approvalRate}
                breakdownText={
                  isDaily
                    ? t(rfidApprovalsTranslations.breakdown.key, rfidApprovalsTranslations.breakdown.defaultValue)
                    : t(
                        rfidApprovalsTranslations.breakdownWeekly.key,
                        rfidApprovalsTranslations.breakdownWeekly.defaultValue,
                      )
                }
                totalPieces={rfidApprovals.negative.totalPieces}
                totalCounts={rfidApprovals.totalCounts}
                approvedPiecesText={t(
                  rfidApprovalsTranslations.approvedPiecesTooltip.key,
                  rfidApprovalsTranslations.approvedPiecesTooltip.defaultValue,
                )}
                nonApprovedPiecesText={t(
                  rfidApprovalsTranslations.nonApprovedPiecesTooltip.key,
                  rfidApprovalsTranslations.nonApprovedPiecesTooltip.defaultValue,
                )}
              />

              <MetricItem
                isDaily={isDaily}
                percentValue={rfidApprovals.positive.difference}
                titleText={t(
                  rfidApprovalsTranslations.positiveTitle.key,
                  rfidApprovalsTranslations.positiveTitle.defaultValue,
                )}
                pieces={rfidApprovals.positive.approvedPieces}
                additionalPieces={rfidApprovals.positive.extraPieces}
                descriptionText={t(
                  rfidApprovalsTranslations.positiveDescription.key,
                  rfidApprovalsTranslations.positiveDescription.defaultValue,
                )}
                approvalRateText={t(
                  rfidApprovalsTranslations.approvalRate.key,
                  rfidApprovalsTranslations.approvalRate.defaultValue,
                )}
                approvalRate={rfidApprovals.positive.approvalRate}
                breakdownText={
                  isDaily
                    ? t(rfidApprovalsTranslations.breakdown.key, rfidApprovalsTranslations.breakdown.defaultValue)
                    : t(
                        rfidApprovalsTranslations.breakdownWeekly.key,
                        rfidApprovalsTranslations.breakdownWeekly.defaultValue,
                      )
                }
                totalPieces={rfidApprovals.positive.totalPieces}
                totalCounts={rfidApprovals.totalCounts}
                approvedPiecesText={t(
                  rfidApprovalsTranslations.approvedPiecesTooltip.key,
                  rfidApprovalsTranslations.approvedPiecesTooltip.defaultValue,
                )}
                nonApprovedPiecesText={t(
                  rfidApprovalsTranslations.nonApprovedPiecesTooltip.key,
                  rfidApprovalsTranslations.nonApprovedPiecesTooltip.defaultValue,
                )}
              />
            </>
          )}
        </RowFlexContainer>
      }
    />
  );
});
